<script setup lang="ts">
import { SolAlert, SolButton, useToast } from '@solfacil/girassol'
import IconDelete from '~icons/material-symbols/delete-outline'
import CancelEquipmentEditModal from '~/components/customer/modals/CancelEquipmentEditModal.vue'
import UpdateEquipmentDetailsModal from '~/components/customer/modals/UpdateEquipmentDetailsModal.vue'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import useContractCancel from '~/store-v2/contract-formalization/useContractCancel'
import useContractResend from '~/store-v2/contract-formalization/useContractResend'

const props = withDefaults(defineProps<{
  title: string
  description: string
  // Blocks resend/delete of the pending TAF; "view details" stays available
  readOnly?: boolean
}>(), {
  readOnly: false,
})

const { t } = useI18n()
const route = useRoute()
const toast = useToast()

// @ts-expect-error - route.params.id exist in typed routes
const projectId = computed(() => String(route.params.id))

const {
  mutateAsync: mutateContractResend,
  isPending: isPendingContractResend,
} = useContractResend(projectId)

const {
  mutateAsync: mutateContractCancel,
  isPending: isPendingContractCancel,
} = useContractCancel(projectId)

const { pendingUpdateEquipment, hasPendingTAF } = usePendingUpdateEquipment(projectId)

const showUpdateEquipmentDetailsModal = ref(false)
const showCancelEquipmentEditModal = ref(false)

async function handleResendTerm() {
  if (props.readOnly)
    return

  try {
    await mutateContractResend()
    toast.createSuccessToast({
      title: t('customer.equipment_update.toast.resend_success_title'),
      description: t('customer.equipment_update.toast.resend_success_description'),
    })
    showUpdateEquipmentDetailsModal.value = false
  }
  catch (error) {
    console.error(error)
    toast.createErrorToast({
      title: 'Erro ao reenviar termo',
      description: 'Ocorreu um erro ao reenviar o termo de aditamento do financiamento. Tente novamente.',
    })
  }
}

function handleCancelUpdateEquipment() {
  if (props.readOnly)
    return

  showUpdateEquipmentDetailsModal.value = false
  showCancelEquipmentEditModal.value = true
}

async function confirmCancelUpdateEquipment() {
  if (props.readOnly)
    return

  try {
    await mutateContractCancel()
    toast.createSuccessToast({
      title: t('customer.equipment_update.toast.cancel_success_title'),
      description: t('customer.equipment_update.toast.cancel_success_description'),
    })
    showCancelEquipmentEditModal.value = false
  }
  catch (error) {
    console.error(error)
    toast.createErrorToast({
      title: t('customer.equipment_update.toast.cancel_error_title'),
      description: t('customer.equipment_update.toast.cancel_error_description'),
    })
  }
}

function handleBackToDetails() {
  showCancelEquipmentEditModal.value = false
  showUpdateEquipmentDetailsModal.value = true
}

// The cancel confirmation modal has no disabled state, so close it when read-only kicks in
watch(() => props.readOnly, (readOnly) => {
  if (readOnly)
    showCancelEquipmentEditModal.value = false
})
</script>

<template>
  <div>
    <SolAlert
      v-if="hasPendingTAF"
      id="pending-update-equipment-alert"
      feedback="warning"
    >
      <div class="flex gap-4 justify-between w-full flex-col md:system:flex-row items-start md:system:items-center">
        <div class="overflow-hidden">
          <p class="font-bold !m-0">
            {{ title }}
          </p>
          <p class="mt-2 !mr-0 !ml-0">
            {{ description }}
          </p>
        </div>
        <div class="flex gap-2 items-center justify-end">
          <SolButton
            id="view-update-equipment-details"
            class="whitespace-nowrap"
            size="small"
            variant="secondary"
            @click="showUpdateEquipmentDetailsModal = true"
          >
            {{ t('customer.equipment_update.button.view_details') }}
          </SolButton>
          <SolButton
            id="resend-update-equipment-term"
            class="whitespace-nowrap"
            size="small"
            variant="secondary"
            :disabled="readOnly || isPendingContractResend"
            @click="handleResendTerm"
          >
            {{ t('customer.equipment_update.button.resend_term') }}
          </SolButton>
          <SolButton
            id="delete-update-equipment"
            class="whitespace-nowrap"
            size="small"
            variant="secondary"
            :disabled="readOnly || isPendingContractCancel"
            @click="handleCancelUpdateEquipment"
          >
            <template #icon:left>
              <IconDelete />
            </template>
          </SolButton>
        </div>
      </div>
    </SolAlert>

    <UpdateEquipmentDetailsModal
      v-if="pendingUpdateEquipment"
      :is-open="showUpdateEquipmentDetailsModal"
      :update-equipment="pendingUpdateEquipment.target_hardware_state"
      :is-pending-contract-resend="isPendingContractResend"
      :is-pending-contract-cancel="isPendingContractCancel"
      :read-only="readOnly"
      @close="showUpdateEquipmentDetailsModal = false"
      @resend-term="handleResendTerm"
      @cancel-update-equipment="handleCancelUpdateEquipment"
    />

    <CancelEquipmentEditModal
      :is-open="showCancelEquipmentEditModal"
      :is-loading="isPendingContractCancel"
      title="Cancelar solicitação de alteração"
      description="Ao cancelar, a alteração de equipamentos que você solicitou será descartada."
      confirm-text="Confirmar Cancelamento"
      cancel-text="Voltar"
      @close="showCancelEquipmentEditModal = false"
      @confirm="confirmCancelUpdateEquipment"
      @cancel="handleBackToDetails"
    />
  </div>
</template>
